import * as THREE from 'three';

/**
 * Base ParticleSystem class for all visualizations
 * Uses BufferGeometry and Points for optimal performance
 *
 * All scenes share the same particle rendering with:
 * - Wide size variation (small to large particles)
 * - Continuous 3D movement without mouse interaction
 * - Consistent visual style across all slides
 */
export class ParticleSystem {
  constructor(count = 5000) {
    this.count = count;
    this.geometry = null;
    this.material = null;
    this.points = null;
    this.uniforms = {};

    this.init();
  }

  init() {
    // Create geometry with position, color, and custom attributes
    this.geometry = new THREE.BufferGeometry();

    const positions = new Float32Array(this.count * 3);
    const colors = new Float32Array(this.count * 3);
    const sizes = new Float32Array(this.count);
    const phases = new Float32Array(this.count);

    // Initialize with random positions in a sphere
    for (let i = 0; i < this.count; i++) {
      const i3 = i * 3;

      // Spherical distribution
      const radius = Math.random() * 50;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      positions[i3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i3 + 2] = radius * Math.cos(phi);

      // Warm off-white (matching --text-primary: #f5f0e6)
      colors[i3] = 0.96;
      colors[i3 + 1] = 0.94;
      colors[i3 + 2] = 0.90;

      // Wide size variation - from tiny to large
      const randomVariation = 0.3 + Math.random() * 3.0; // 0.3x to 3.3x
      sizes[i] = (0.5 + Math.random() * 10.0) * randomVariation;

      phases[i] = Math.random() * Math.PI * 2;
    }

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    this.geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1));

    // Store original positions for morphing
    this.originalPositions = positions.slice();

    // Setup uniforms
    this.uniforms = {
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(0, 0) },
      uMouseRadius: { value: 0.15 },
      uMorphProgress: { value: 0 },
      uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
      uSize: { value: 60 }
    };

    // Create shader material
    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: this.getVertexShader(),
      fragmentShader: this.getFragmentShader(),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      vertexColors: true
    });

    // Create points
    this.points = new THREE.Points(this.geometry, this.material);
  }

  // Shared vertex shader with continuous 3D movement
  getVertexShader() {
    return `
      uniform float uTime;
      uniform vec2 uMouse;
      uniform float uMouseRadius;
      uniform float uPixelRatio;
      uniform float uSize;

      attribute float size;
      attribute float aPhase;

      varying vec3 vColor;
      varying float vAlpha;

      void main() {
        vec3 pos = position;

        // Continuous 3D movement - floating/drifting motion
        float speed = 0.3;
        pos.x += sin(uTime * speed + aPhase) * 3.0;
        pos.y += cos(uTime * speed * 0.8 + aPhase * 1.3) * 2.5;
        pos.z += sin(uTime * speed * 0.6 + aPhase * 0.7) * 8.0;

        // Additional wave motion for depth
        pos.z += sin(pos.x * 0.05 + uTime * 0.5) * 3.0;
        pos.z += cos(pos.y * 0.05 + uTime * 0.4) * 3.0;

        vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);

        // Size with breathing effect
        float psize = size * uSize * uPixelRatio;
        psize *= (1.0 + sin(uTime * 0.5 + aPhase) * 0.2);
        psize *= (1.0 / -mvPosition.z);

        gl_PointSize = clamp(psize, 2.0, 150.0);
        gl_Position = projectionMatrix * mvPosition;

        vColor = color;
        vAlpha = 0.85 + sin(uTime * 0.3 + aPhase) * 0.1;
      }
    `;
  }

  getFragmentShader() {
    return `
      varying vec3 vColor;
      varying float vAlpha;

      void main() {
        // Soft circular particle
        vec2 uv = gl_PointCoord - 0.5;
        float dist = length(uv);

        // Soft falloff
        float alpha = 1.0 - smoothstep(0.3, 0.5, dist);
        alpha *= vAlpha;

        gl_FragColor = vec4(vColor, alpha);
      }
    `;
  }

  update(time, deltaTime) {
    this.uniforms.uTime.value = time;
  }

  updateMouse(x, y) {
    this.uniforms.uMouse.value.set(x, y);
  }

  setMorphProgress(progress) {
    this.uniforms.uMorphProgress.value = progress;
  }

  getMesh() {
    return this.points;
  }

  getPoints() {
    return this.points;
  }

  dispose() {
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
  }
}

// Shared vertex shader code that all scenes should use
export const SHARED_VERTEX_SHADER = `
  uniform float uTime;
  uniform float uPixelRatio;
  uniform float uSize;

  attribute float size;
  attribute float aPhase;

  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    vec3 pos = position;

    // Continuous 3D movement - floating/drifting motion
    float speed = 0.3;
    pos.x += sin(uTime * speed + aPhase) * 3.0;
    pos.y += cos(uTime * speed * 0.8 + aPhase * 1.3) * 2.5;
    pos.z += sin(uTime * speed * 0.6 + aPhase * 0.7) * 8.0;

    // Additional wave motion for depth
    pos.z += sin(pos.x * 0.05 + uTime * 0.5) * 3.0;
    pos.z += cos(pos.y * 0.05 + uTime * 0.4) * 3.0;

    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);

    // Size with breathing effect
    float psize = size * uSize * uPixelRatio;
    psize *= (1.0 + sin(uTime * 0.5 + aPhase) * 0.2);
    psize *= (1.0 / -mvPosition.z);

    gl_PointSize = clamp(psize, 2.0, 150.0);
    gl_Position = projectionMatrix * mvPosition;

    vColor = color;
    vAlpha = 0.85 + sin(uTime * 0.3 + aPhase) * 0.1;
  }
`;

export const SHARED_FRAGMENT_SHADER = `
  varying vec3 vColor;
  varying float vAlpha;

  void main() {
    vec2 uv = gl_PointCoord - 0.5;
    float dist = length(uv);
    float alpha = 1.0 - smoothstep(0.3, 0.5, dist);
    alpha *= vAlpha;
    gl_FragColor = vec4(vColor, alpha);
  }
`;
